/**
 * ThrownWeaponVisual — pure descriptor for the cosmetic in-flight visual of a
 * thrown weapon (molotov / brick / throwing-knife). No Phaser — node-testable.
 *
 * Gameplay is unchanged: Player.throwWeapon still spawns an instant ranged
 * hitbox. This only drives the StageScene fly-across animation so the player
 * SEES the weapon travel instead of it being invisible.
 */
import type { ThrownWeaponKind } from '@/data/weapons';

export interface ThrownWeaponVisual {
  kind: ThrownWeaponKind;
  /** Body colour (from the WeaponDef). */
  color: number;
  /** Tumbles end-over-end in flight (knife + brick); molotov stays roughly upright. */
  spin: boolean;
  /** Trailing flame flicker (molotov only). */
  flame: boolean;
  /** Lobs on a gravity arc (molotov + brick); the knife flies straight. */
  arc: boolean;
  /** Flight duration in ms (visual only — the hitbox is instantaneous). */
  durationMs: number;
}

/**
 * Build the in-flight visual descriptor for a weapon, or null for a melee weapon
 * (no `thrownKind` → nothing flies).
 */
export function thrownWeaponVisual(def: {
  thrownKind?: ThrownWeaponKind;
  color: number;
}): ThrownWeaponVisual | null {
  if (!def.thrownKind) return null;
  const kind = def.thrownKind;
  const straight = kind === 'throwing-knife';
  return {
    kind,
    color: def.color,
    spin: kind !== 'molotov',
    flame: kind === 'molotov',
    arc: !straight,
    durationMs: straight ? 130 : 200,
  };
}
